import { CreditCard, Files, LayoutDashboard, Mails, ReceiptText, Settings } from "lucide-react";

import type { ConciliationTabGroups } from "@/@types/conciliation-tab-groups";
import type { PathRedirect, TabGroupFactory } from "@/@types/tab-navigation";
import { GroupsEnum } from "@/enum/groups";
import { RoleEnum } from "@/enum/role";
import AccountingAccountRoutes from "@/pages/conciliation/accounting-account/justification.route";
import AnalystAssignmentRoutes from "@/pages/conciliation/analyst-assignment/analyst-assignment.route";
import AuditTrail from "@/pages/conciliation/audit-trail";
import DashboardConciliacao from "@/pages/conciliation/dashboard-conciliacao";
import { DtefImport } from "@/pages/conciliation/dtef-import";
import DtefValidation from "@/pages/conciliation/dtef-validation";
import EmailReport from "@/pages/conciliation/email-report";
import JustificationRoutes from "@/pages/conciliation/justification/justification.route";
import LateReconciliationRoutes from "@/pages/conciliation/late-reconciliation/late-reconciliation.route";
import RegionalRoutes from "@/pages/conciliation/regional/regional-route";
import ReportGenerator from "@/pages/conciliation/report-generator";
import SectorEmails from "@/pages/conciliation/sector-emails";
import StoreEmail from "@/pages/conciliation/store-email";
import CashMovementListingRoutes from "@/pages/financial/cash-movement-listing/cash-movement-listing.routes";
import SoldItemsListingRoutes from "@/pages/financial/sold-items-listing/sold-items-listing.routes";

import { CONCILIATION_CATEGORIES } from "./categories";
import { CONCILIATION_TAB_KEYS as TAB_KEYS } from "./tab-keys";
import { PATH_IDS } from "../path-ids";
import { createTabGroupFactory } from "../tab-group-factory";

const ANALYST_AND_ADMIN: RoleEnum[] = [RoleEnum.ANALISTA, RoleEnum.ADMINISTRADOR];
const ADMIN_ONLY: RoleEnum[] = [RoleEnum.ADMINISTRADOR];
const CONCILIATION_ONLY: GroupsEnum[] = [GroupsEnum.CONCILIACAO];

export const CONCILIATION_TAB_GROUPS: ConciliationTabGroups = {
  RELATORIOS_GERAIS: {
    label: "Relatórios Gerais",
    category: CONCILIATION_CATEGORIES.RELATORIOS,
    path: "/relatorios-gerais",
    Icon: Files,
    tabs: [
      {
        key: TAB_KEYS.RELATORIOS_GERAIS.CONCILIACOES_ATRASADAS,
        label: "Conciliações Atrasadas",
        path: TAB_KEYS.RELATORIOS_GERAIS.CONCILIACOES_ATRASADAS,
        previousPath: "/conciliacoes-atrasadas",
        requiredRole: ANALYST_AND_ADMIN,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <LateReconciliationRoutes />,
      },
      {
        key: TAB_KEYS.RELATORIOS_GERAIS.GERACAO_DE_EXCEL,
        label: "Geração de Excel",
        path: TAB_KEYS.RELATORIOS_GERAIS.GERACAO_DE_EXCEL,
        previousPath: "/relatorios-excel",
        requiredRole: ADMIN_ONLY,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <ReportGenerator />,
      },
    ],
  },
  RELATORIOS_VAR: {
    label: "Relatórios VAR",
    category: CONCILIATION_CATEGORIES.RELATORIOS,
    path: "/relatorios-var",
    Icon: ReceiptText,
    tabs: [
      {
        key: TAB_KEYS.RELATORIOS_VAR.MOVIMENTO_DE_CAIXA,
        label: "Movimento de Caixa e Crediário",
        path: TAB_KEYS.RELATORIOS_VAR.MOVIMENTO_DE_CAIXA,
        previousPath: "/movimento-de-caixa",
        requiredRole: ANALYST_AND_ADMIN,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <CashMovementListingRoutes />,
      },
      {
        key: TAB_KEYS.RELATORIOS_VAR.LISTAGEM_ITENS_VENDIDOS,
        label: "Listagem de Itens Vendidos",
        path: TAB_KEYS.RELATORIOS_VAR.LISTAGEM_ITENS_VENDIDOS,
        previousPath: "/listagem-itens-vendidos",
        requiredRole: ANALYST_AND_ADMIN,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <SoldItemsListingRoutes />,
      },
    ],
  },
  EMAILS: {
    label: "E-mails",
    category: CONCILIATION_CATEGORIES.ADMINISTRATIVO,
    path: "/emails",
    Icon: Mails,
    tabs: [
      {
        key: TAB_KEYS.EMAILS.EMAILS_DE_LOJA,
        label: "E-mails de Loja",
        path: TAB_KEYS.EMAILS.EMAILS_DE_LOJA,
        previousPath: "/emails-de-loja",
        requiredRole: ADMIN_ONLY,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <StoreEmail />,
      },
      {
        key: TAB_KEYS.EMAILS.HISTORICO,
        label: "Histórico de E-mails",
        path: TAB_KEYS.EMAILS.HISTORICO,
        previousPath: "/controle-emails",
        requiredRole: ADMIN_ONLY,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <EmailReport />,
      },
      {
        key: TAB_KEYS.EMAILS.EMAILS_DE_SETORES,
        label: "E-mails de Setores",
        path: TAB_KEYS.EMAILS.EMAILS_DE_SETORES,
        previousPath: "/emails-de-setores",
        requiredRole: ADMIN_ONLY,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <SectorEmails />,
      },
    ],
  },
  CONFIGURACOES: {
    label: "Configurações",
    category: CONCILIATION_CATEGORIES.ADMINISTRATIVO,
    path: "/configuracoes",
    Icon: Settings,
    tabs: [
      {
        key: TAB_KEYS.CONFIGURACOES.ATRIBUICAO_DE_ANALISTA,
        label: "Atribuição de Analista",
        path: TAB_KEYS.CONFIGURACOES.ATRIBUICAO_DE_ANALISTA,
        previousPath: "/atribuicoes-de-analistas",
        requiredRole: ADMIN_ONLY,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <AnalystAssignmentRoutes />,
      },
      {
        key: TAB_KEYS.CONFIGURACOES.REGIONAIS,
        label: "Regionais",
        path: TAB_KEYS.CONFIGURACOES.REGIONAIS,
        previousPath: "/regionais",
        requiredRole: ADMIN_ONLY,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <RegionalRoutes />,
      },
      {
        key: TAB_KEYS.CONFIGURACOES.JUSTIFICATIVAS,
        label: "Justificativa",
        path: TAB_KEYS.CONFIGURACOES.JUSTIFICATIVAS,
        previousPath: "/justificativas",
        requiredRole: ADMIN_ONLY,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <JustificationRoutes />,
      },
      {
        key: TAB_KEYS.CONFIGURACOES.CONTA_CONTABIL,
        label: "Conta Contábil",
        path: TAB_KEYS.CONFIGURACOES.CONTA_CONTABIL,
        previousPath: "/conta-contabil",
        requiredRole: ADMIN_ONLY,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <AccountingAccountRoutes />,
      },
    ],
  },
  DASHBOARD: {
    label: "Dashboard",
    category: CONCILIATION_CATEGORIES.ADMINISTRATIVO,
    path: "/dashboard",
    Icon: LayoutDashboard,
    tabs: [
      {
        key: TAB_KEYS.DASHBOARD.PAINEL,
        label: "Dashboard",
        path: TAB_KEYS.DASHBOARD.PAINEL,
        requiredRole: ADMIN_ONLY,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <DashboardConciliacao />,
      },
      {
        key: TAB_KEYS.DASHBOARD.TRILHA_DE_AUDITORIA,
        label: "Trilha de Auditoria",
        path: TAB_KEYS.DASHBOARD.TRILHA_DE_AUDITORIA,
        previousPath: "/trilha-de-auditoria",
        requiredRole: ADMIN_ONLY,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <AuditTrail />,
      },
    ],
  },
  DTEF: {
    label: "DTEF",
    category: CONCILIATION_CATEGORIES.ADMINISTRATIVO,
    path: "/dtef",
    Icon: CreditCard,
    tabs: [
      {
        key: TAB_KEYS.DTEF.PENDENCIAS,
        label: "Pendências DTEF",
        path: TAB_KEYS.DTEF.PENDENCIAS,
        previousPath: "/validacao-dtef",
        requiredRole: ADMIN_ONLY,
        groupRequired: CONCILIATION_ONLY,
        requiredAction: [],
        element: <DtefValidation />,
      },
      {
        key: TAB_KEYS.DTEF.FORMAS_DE_PAGAMENTO,
        label: "Formas de Pagamento",
        path: TAB_KEYS.DTEF.FORMAS_DE_PAGAMENTO,
        requiredRole: ANALYST_AND_ADMIN,
        groupRequired: [GroupsEnum.CONCILIACAO, GroupsEnum.FINANCEIRO],
        requiredAction: [],
        element: <DtefImport />,
      },
    ],
  },
};

export const conciliationTabGroups: TabGroupFactory = createTabGroupFactory({
  pathId: PATH_IDS.CONCILIACAO,
  groupRequired: CONCILIATION_ONLY,
});

export const CONCILIATION_PATH_REDIRECTS: PathRedirect[] = conciliationTabGroups.buildRedirects(
  Object.values(CONCILIATION_TAB_GROUPS),
);
